import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/soul";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Your password has been renewed. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="reset-page" className="min-h-[calc(100vh-73px)] grid place-items-center px-6 py-16">
      <div className="w-full max-w-md glass rounded-2xl p-8 sm:p-10 gold-glow fade-up">
        <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-3">A new beginning</p>
        <h1 className="serif text-4xl text-[#F5F1E7] font-light">Choose a new password</h1>

        {!token ? (
          <p data-testid="reset-no-token" className="mt-6 text-[#A8A29E]">
            This reset link is missing or invalid. Please{" "}
            <Link to="/forgot-password" className="text-[#D4AF37] hover:underline">request a new one</Link>.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-[#A8A29E] mb-2 block">New password</span>
              <input
                data-testid="reset-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-[#F5F1E7] outline-none focus:border-[#D4AF37]/60 transition-colors duration-300 placeholder:text-[#A8A29E]/50"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-[#A8A29E] mb-2 block">Confirm password</span>
              <input
                data-testid="reset-confirm-input"
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-[#F5F1E7] outline-none focus:border-[#D4AF37]/60 transition-colors duration-300 placeholder:text-[#A8A29E]/50"
              />
            </label>
            {error && <p data-testid="reset-error" className="text-sm text-[#C1272D]">{error}</p>}
            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#D4AF37] text-[#110D14] px-7 py-3.5 font-medium hover:bg-[#e6c860] transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? "Renewing..." : "Renew password"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
